import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, ChevronDown, ChevronUp, MessageSquare, Send, LogOut } from "lucide-react";
import { FaWrench, FaBolt, FaBug, FaPaintBrush, FaTools, FaCog } from "react-icons/fa";
import { MdElevator, MdPlumbing, MdElectricalServices } from "react-icons/md";
import { API_BASE } from '../config'
import { getPortalRoute, normalizeRole } from '../utils/roleRouting'

async function apiFetch(path, opts = {}) {
  const token = sessionStorage.getItem("id_token");
  const resp = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...opts.headers },
  });
  if (!resp.ok) throw new Error(`API ${resp.status}`);
  return resp.json();
}

const CATEGORIES = [
  { value: "plumbing", label: "Plumbing", icon: MdPlumbing },
  { value: "electrical", label: "Electrical", icon: MdElectricalServices },
  { value: "carpentry", label: "Carpentry", icon: FaTools },
  { value: "painting", label: "Painting", icon: FaPaintBrush },
  { value: "pest_control", label: "Pest Control", icon: FaBug },
  { value: "elevator", label: "Elevator", icon: MdElevator },
  { value: "general", label: "General", icon: FaCog },
  { value: "other", label: "Other", icon: FaWrench },
];

const PRIORITIES = [
  { value: "low", label: "Low", color: "bg-gray-100 text-gray-700" },
  { value: "medium", label: "Medium", color: "bg-yellow-100 text-yellow-700" },
  { value: "high", label: "High", color: "bg-orange-100 text-orange-700" },
  { value: "urgent", label: "Urgent", color: "bg-red-100 text-red-700" },
];

const STATUS_BADGES = {
  open: "bg-blue-100 text-blue-800",
  assigned: "bg-blue-100 text-blue-800",
  in_progress: "bg-yellow-100 text-yellow-800",
  resolved: "bg-green-100 text-green-800",
  closed: "bg-gray-100 text-gray-600",
  reopened: "bg-red-100 text-red-800",
};

// Staff can only transition to these statuses
const STAFF_STATUS_OPTIONS = [
  { value: "in_progress", label: "In Progress" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

function formatDate(val) {
  if (!val) return "";
  try {
    const d = new Date(val);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return val; }
}

function getCategoryIcon(cat) {
  const found = CATEGORIES.find(c => c.value === cat);
  if (found) {
    const Icon = found.icon;
    return <Icon size={16} />;
  }
  return <FaWrench size={16} />;
}

function decodeIdTokenEmail() {
  try {
    const t = sessionStorage.getItem("id_token");
    if (!t) return null;
    const payload = t.split(".")[1];
    const json = decodeURIComponent(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
        .split("")
        .map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export default function ServiceStaffPortal() {
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [user, setUser] = useState(null);

  // Comment state
  const [commentIssueId, setCommentIssueId] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [commenting, setCommenting] = useState(false);

  const communityId = sessionStorage.getItem("community_id") || "";
  const tokenPayload = decodeIdTokenEmail();
  const staffEmail = (tokenPayload?.email || "").toLowerCase();
  const staffName = tokenPayload?.name || tokenPayload?.given_name || staffEmail.split("@")[0] || "Staff";

  function showToast(message, type = "success") {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(x => x.id !== id)), 3500);
  }

  // Auth check
  useEffect(() => {
    (async () => {
      try {
        const idToken = sessionStorage.getItem("id_token");
        if (!idToken) { navigate("/"); return; }

        if (!staffEmail) { navigate("/access-denied"); return; }

        const resp = await fetch(`${API_BASE}/reg-user?emailId=${encodeURIComponent(staffEmail)}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (!resp.ok) { navigate("/access-denied"); return; }

        let data = await resp.json();
        const returned = ((data.emailId || data.email || data.Email || data.EMAIL) || "").toLowerCase();
        if (returned !== staffEmail) { navigate("/access-denied"); return; }

        // Multi-community: resolve the record that was selected in the community picker
        if (data.multiCommunity && Array.isArray(data.communities)) {
          const selectedCid = sessionStorage.getItem('community_id') || '';
          const selectedUid = sessionStorage.getItem('user_id') || '';
          const match = data.communities.find(
            c => {
              const cid = c.COMMUNITY_ID || c.communityId || '';
              const uid = c.USER_ID || c.userId || '';
              return selectedUid ? (cid === selectedCid && uid === selectedUid) : (cid === selectedCid);
            }
          ) || data.communities[0];
          if (!match) { navigate('/access-denied'); return; }
          data = match;
        }

        const role = normalizeRole(data.ROLE || data.role);
        const expectedRoute = getPortalRoute(data, { fallback: '/access-denied' });
        if (expectedRoute !== '/service-staff') {
          navigate(expectedRoute);
          return;
        }

        const cid = data.COMMUNITY_ID || data.communityId || "";
        if (cid) sessionStorage.setItem("community_id", cid);
        const userId = data.USER_ID || data.userId || "";
        if (userId) sessionStorage.setItem("user_id", userId);
        sessionStorage.setItem("user_role", role);
        setUser(data);
      } catch (err) {
        console.error("auth check failed:", err);
        navigate("/access-denied");
      }
    })();
  }, [navigate, staffEmail]);

  async function loadIssues() {
    if (!communityId || !staffEmail) return;
    setLoading(true);
    try {
      const data = await apiFetch(`/issues?communityId=${encodeURIComponent(communityId)}&assignee=${encodeURIComponent(staffEmail)}`);
      setIssues(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("loadIssues error:", err);
      showToast("Failed to load issues", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (communityId && staffEmail) loadIssues();
  }, [communityId, staffEmail]);

  async function handleStatusChange(issue, newStatus) {
    try {
      await apiFetch("/issues", {
        method: "PATCH",
        body: JSON.stringify({
          COMMUNITY_ID: communityId,
          ISSUE_ID: issue.ISSUE_ID,
          STATUS: newStatus,
        }),
      });
      showToast(`Status updated to ${newStatus.replace("_", " ")}`);
      loadIssues();
    } catch (err) {
      console.error("status change error:", err);
      showToast("Failed to update status", "error");
    }
  }

  async function handleComment(issueId) {
    if (!commentText.trim()) return;
    setCommenting(true);
    try {
      await apiFetch("/issues", {
        method: "PATCH",
        body: JSON.stringify({
          COMMUNITY_ID: communityId,
          ISSUE_ID: issueId,
          COMMENT: {
            AUTHOR: staffEmail,
            AUTHOR_NAME: staffName,
            TEXT: commentText.trim(),
          },
        }),
      });
      showToast("Comment added");
      setCommentText("");
      setCommentIssueId(null);
      loadIssues();
    } catch (err) {
      console.error("comment error:", err);
      showToast("Failed to add comment", "error");
    } finally {
      setCommenting(false);
    }
  }

  async function handleSignOut() {
    let token = sessionStorage.getItem("access_token");
    if (!token) token = sessionStorage.getItem("id_token");
    if (token) {
      try {
        await fetch("https://oauth2.googleapis.com/revoke", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: `token=${encodeURIComponent(token)}`,
        });
      } catch {}
    }
    sessionStorage.removeItem("id_token");
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("community_id");
    sessionStorage.removeItem("unit_nbr");
    sessionStorage.removeItem("user_id");
    sessionStorage.removeItem("user_role");
    navigate("/");
  }

  // Stats
  const activeIssues = issues.filter(i => i.STATUS !== "closed" && i.STATUS !== "resolved");
  const statusCounts = {
    all: issues.length,
    assigned: issues.filter(i => i.STATUS === "assigned").length,
    in_progress: issues.filter(i => i.STATUS === "in_progress").length,
    resolved: issues.filter(i => i.STATUS === "resolved").length,
    closed: issues.filter(i => i.STATUS === "closed").length,
  };

  const filtered = issues.filter(issue => {
    if (filterStatus === "all") return true;
    if (filterStatus === "active") return issue.STATUS !== "closed" && issue.STATUS !== "resolved";
    return issue.STATUS === filterStatus;
  });

  return (
    <div className="min-h-screen bg-gray-50 portal-bg">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-[100] space-y-2">
        {toasts.map(t => (
          <div key={t.id} className={`px-4 py-2 rounded-lg shadow-lg text-white text-sm ${t.type === "error" ? "bg-red-500" : "bg-green-500"}`}>
            {t.message}
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="bg-blue-600 text-white p-4 sticky top-0 z-50 safe-top safe-left safe-right">
        <div className="flex items-center justify-between max-w-2xl mx-auto px-2">
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold truncate">{sessionStorage.getItem('community_name') || 'Smart Community Manager'}</h1>
            <p className="text-xs text-blue-200">{sessionStorage.getItem('community_name') ? 'Powered by Smart Community Manager' : 'Service Staff Portal'}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs font-medium truncate max-w-[120px]">{staffName}</p>
              <p className="text-[10px] text-blue-200 truncate max-w-[120px]">{staffEmail}</p>
            </div>
            <button
              onClick={handleSignOut}
              className="bg-white/20 hover:bg-white/30 p-2 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <p className="text-2xl font-bold text-blue-700">{activeIssues.length}</p>
            <p className="text-xs text-blue-600">Active Issues</p>
          </div>
          <div className="bg-green-50 border border-green-100 rounded-lg p-4">
            <p className="text-2xl font-bold text-green-700">{statusCounts.resolved + statusCounts.closed}</p>
            <p className="text-xs text-green-600">Completed</p>
          </div>
        </div>

        {/* Header + Refresh */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">My Assigned Issues</h2>
          <button onClick={loadIssues} className="p-2 rounded-lg bg-gray-100 active:bg-gray-200">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        {/* Filter Pills */}
        <div className="filter-pills pb-3">
          {[
            { key: "all", label: "All" },
            { key: "active", label: "Active" },
            { key: "assigned", label: "New" },
            { key: "in_progress", label: "In Progress" },
            { key: "resolved", label: "Resolved" },
            { key: "closed", label: "Closed" },
          ].map(s => (
            <button
              key={s.key}
              onClick={() => setFilterStatus(s.key)}
              className={`filter-pill px-3 py-1 rounded-full text-xs font-medium ${
                filterStatus === s.key ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"
              }`}
            >
              {s.label} ({s.key === "active" ? activeIssues.length : (statusCounts[s.key] || 0)})
            </button>
          ))}
        </div>

        {/* Issues */}
        {loading && issues.length === 0 ? (
          <div className="text-center py-8 text-gray-400">Loading your issues...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <FaWrench size={28} className="mx-auto mb-2 text-gray-300" />
            <p className="text-center text-sm text-white py-8">No issues found</p>
            <p className="text-xs mt-1">Issues assigned to you will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(issue => {
              const isExpanded = expandedId === issue.ISSUE_ID;
              const comments = Array.isArray(issue.COMMENTS) ? issue.COMMENTS : [];
              const priorityConfig = PRIORITIES.find(p => p.value === issue.PRIORITY) || PRIORITIES[1];
              const isUrgent = issue.PRIORITY === "urgent" || issue.PRIORITY === "high";

              return (
                <div
                  key={issue.ISSUE_ID}
                  className={`bg-white border rounded-lg overflow-hidden ${isUrgent ? "border-red-200" : "border-gray-200"}`}
                >
                  <button
                    className="w-full p-4 text-left"
                    onClick={() => setExpandedId(isExpanded ? null : issue.ISSUE_ID)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`mt-0.5 ${isUrgent ? "text-red-500" : "text-gray-500"}`}>
                          {getCategoryIcon(issue.CATEGORY)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{issue.TITLE}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_BADGES[issue.STATUS] || STATUS_BADGES.assigned}`}>
                              {(issue.STATUS || "assigned").replace("_", " ").toUpperCase()}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${priorityConfig.color}`}>
                              {priorityConfig.label}
                            </span>
                            <span className="text-[10px] text-gray-400">Unit {issue.UNIT_NBR}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        {comments.length > 0 && (
                          <span className="flex items-center gap-1 text-gray-400 text-xs">
                            <MessageSquare size={12} /> {comments.length}
                          </span>
                        )}
                        {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                      </div>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-gray-100 px-4 pb-4">
                      {/* Description */}
                      {issue.DESCRIPTION && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-gray-500 mb-1">Description</p>
                          <p className="text-sm text-gray-700">{issue.DESCRIPTION}</p>
                        </div>
                      )}

                      {/* Details */}
                      <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
                        <div>
                          <p className="text-gray-500">Category</p>
                          <p className="font-medium capitalize">{(issue.CATEGORY || "").replace("_", " ")}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Unit</p>
                          <p className="font-medium">{issue.UNIT_NBR}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Reported By</p>
                          <p className="font-medium">{issue.REPORTED_BY_NAME || issue.REPORTED_BY}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Created</p>
                          <p className="font-medium">{formatDate(issue.CREATED_AT)}</p>
                        </div>
                      </div>

                      {/* Status Actions */}
                      {issue.STATUS !== "closed" && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-gray-500 mb-2">Update Status</p>
                          <div className="flex gap-2 flex-wrap">
                            {STAFF_STATUS_OPTIONS.map(s => (
                              <button
                                key={s.value}
                                onClick={(e) => { e.stopPropagation(); handleStatusChange(issue, s.value); }}
                                disabled={issue.STATUS === s.value}
                                className={`px-3 py-1.5 text-xs rounded-lg font-medium ${
                                  issue.STATUS === s.value
                                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                    : s.value === "in_progress"
                                    ? "bg-yellow-50 text-yellow-700 active:bg-yellow-100"
                                    : s.value === "resolved"
                                    ? "bg-green-50 text-green-700 active:bg-green-100"
                                    : "bg-gray-50 text-gray-700 active:bg-gray-100"
                                }`}
                              >
                                {s.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Comments */}
                      {comments.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-gray-500 mb-2">Comments ({comments.length})</p>
                          <div className="space-y-2">
                            {comments.map((cmt, idx) => (
                              <div key={cmt.COMMENT_ID || idx} className="bg-gray-50 rounded-lg p-2">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-medium text-gray-700">{cmt.AUTHOR_NAME || cmt.AUTHOR || "Unknown"}</span>
                                  <span className="text-[10px] text-gray-400">{formatDate(cmt.CREATED_AT)}</span>
                                </div>
                                <p className="text-xs text-gray-600">{cmt.TEXT}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Add Comment */}
                      <div className="mt-3">
                        {commentIssueId === issue.ISSUE_ID ? (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={commentText}
                              onChange={e => setCommentText(e.target.value)}
                              placeholder="Add a comment or update..."
                              className="flex-1 p-2 text-xs border border-gray-200 rounded-lg"
                              onClick={e => e.stopPropagation()}
                              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleComment(issue.ISSUE_ID); } }}
                            />
                            <button
                              onClick={(e) => { e.stopPropagation(); handleComment(issue.ISSUE_ID); }}
                              disabled={commenting}
                              className="px-3 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
                            >
                              <Send size={14} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setCommentIssueId(issue.ISSUE_ID); }}
                            className="text-xs text-blue-600 font-medium"
                          >
                            + Add Comment
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
